import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { QuoteVersion } from '@/types/quote';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RotateCcw } from 'lucide-react';

interface QuoteVersionModalProps {
  version: QuoteVersion;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore?: () => void;
}

export const QuoteVersionModal = ({ version, open, onOpenChange, onRestore }: QuoteVersionModalProps) => {
  const lineItems = Array.isArray(version.line_items) ? version.line_items : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle>
                Revisie {version.version_number} - {version.title}
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Opgeslagen op {format(new Date(version.created_at), 'PPp', { locale: nl })}
                {version.created_by_profile && ` door ${version.created_by_profile.display_name || version.created_by_profile.email}`}
              </p>
            </div>
            {onRestore && (
              <Button variant="outline" size="sm" onClick={onRestore}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Zet terug naar deze revisie
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {version.notes && (
            <div>
              <h3 className="font-semibold mb-2">Notities</h3>
              <p className="text-sm text-muted-foreground bg-muted p-3 rounded">
                {version.notes}
              </p>
            </div>
          )}

          {version.body && (
            <div>
              <h3 className="font-semibold mb-2">Inhoud</h3>
              <div className="text-sm whitespace-pre-wrap bg-muted p-4 rounded">
                {version.body}
              </div>
            </div>
          )}

          {lineItems.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Regels</h3>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Omschrijving</TableHead>
                      <TableHead className="text-right">Aantal</TableHead>
                      <TableHead className="text-right">Prijs</TableHead>
                      <TableHead className="text-right">BTW</TableHead>
                      <TableHead className="text-right">Totaal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((item: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">€{item.unit_price?.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{item.vat_rate}%</TableCell>
                        <TableCell className="text-right font-medium">
                          €{item.total?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotaal</span>
                  <span>€{version.subtotal_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">BTW</span>
                  <span>€{version.vat_amount?.toFixed(2)}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold text-base">
                  <span>Totaal</span>
                  <span>€{version.total_amount?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {version.terms && (
            <div>
              <h3 className="font-semibold mb-2">Voorwaarden</h3>
              <div className="text-sm whitespace-pre-wrap bg-muted p-4 rounded">
                {version.terms}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
